import { z } from "zod";
import { metaSchema } from "./commun.zod";

export const EspeceBaseSchema = z.object({
  name: z.string().min(1, "Le nom est requis").max(255, "Le nom ne peut pas dépasser 255 caractères").nullable().optional(),
  img_url: z.string().url("URL invalide").max(255, "L'URL de l'image ne peut pas dépasser 255 caractères").nullable().optional(),
  categorie: z.string().max(255, "La catégorie ne peut pas dépasser 255 caractères").nullable().optional(),
  cultivar: z.string().max(255, "Le cultivar ne peut pas dépasser 255 caractères").nullable().optional(),
  nom_fr: z.string().max(255, "Le nom français ne peut pas dépasser 255 caractères").nullable().optional(),
  genre: z.string().max(255, "Le genre ne peut pas dépasser 255 caractères").nullable().optional(),
  tarif: z.number().positive("Le tarif doit être positif").nullable().optional(),
  is_deleted: z.boolean().default(false).optional(),
  indice_espece: z.number().int().nullable().optional(),
  user_added_id: z.number().int().positive("L'ID de l'utilisateur doit être un nombre positif").nullable().optional(),
});

export const EspeceSchema = EspeceBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
});

export const EspeceCreateSchema = EspeceBaseSchema.pick({
  name: true,
  img_url: true,
  categorie: true,
  cultivar: true,
  nom_fr: true,
  genre: true,
  tarif: true,
  is_deleted: true,
  user_added_id: true,
});

export const EspeceUpdateSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1, "Le nom est requis").max(255, "Le nom ne peut pas dépasser 255 caractères").nullable().optional(),
  img_url: z.string().url("URL invalide").max(255, "L'URL de l'image ne peut pas dépasser 255 caractères").nullable().optional(),
  categorie: z.string().max(255, "La catégorie ne peut pas dépasser 255 caractères").nullable().optional(),
  cultivar: z.string().max(255, "Le cultivar ne peut pas dépasser 255 caractères").nullable().optional(),
  nom_fr: z.string().max(255, "Le nom français ne peut pas dépasser 255 caractères").nullable().optional(),
  genre: z.string().max(255, "Le genre ne peut pas dépasser 255 caractères").nullable().optional(),
  tarif: z.number().positive("Le tarif doit être positif").nullable().optional(),
  is_deleted: z.boolean().optional(),
  user_added_id: z.number().int().positive("L'ID de l'utilisateur doit être un nombre positif").nullable().optional(),
});

export const EspeceListResponseSchema = z.object({
  data: z.array(EspeceSchema),
  meta: metaSchema
});

export const EspeceResponseSchema = z.object({
  data: EspeceSchema,
  message: z.string().optional(),
});

export const EspeceSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  name: z.string().optional(),
  categorie: z.string().optional(),
  cultivar: z.string().optional(),
  nom_fr: z.string().optional(),
  genre: z.string().optional(),
  is_deleted: z.boolean().optional(),
  user_added_id: z.number().int().positive().optional(),
  includeDeleted: z.boolean().default(false),
  limit: z.number().default(10),
  page: z.number().default(1),
  search: z.string().optional(),
  sortBy: z.string().optional(),
});

export type Espece = z.infer<typeof EspeceSchema>;
export type EspeceCreateInput = z.infer<typeof EspeceCreateSchema>;
export type EspeceUpdateInput = z.infer<typeof EspeceUpdateSchema>;
export type EspeceSelectInput = z.infer<typeof EspeceSelectSchema>;

// Form-specific types for frontend
export type EspeceFormData = Omit<EspeceCreateInput, 'is_deleted' | 'user_added_id'> & {
  is_deleted?: boolean;
  user_added_id?: number;
  tarif?: string | number; // Allow string for form input
};

export type EspeceEditFormData = Omit<EspeceUpdateInput, 'id'>;

// Utility types for different UI states
export type EspeceDisplayItem = Pick<Espece, 'id' | 'name' | 'nom_fr' | 'categorie' | 'genre'>;
export type EspeceSelectOption = Pick<Espece, 'id' | 'name' | 'nom_fr'>;
export type EspeceTableItem = Pick<Espece, 'id' | 'name' | 'nom_fr' | 'categorie' | 'cultivar' | 'genre' | 'tarif' | 'created_at' | 'img_url'>;

export type EspeceResponse = z.infer<typeof EspeceResponseSchema>;
export type EspeceListResponse = z.infer<typeof EspeceListResponseSchema>;

export const validateEspeceCreate = (data: unknown) => {
  return EspeceCreateSchema.safeParse(data);
};

export const validateEspeceUpdate = (data: unknown) => {
  return EspeceUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validateEspeceSelect = (data: unknown) => {
  return EspeceSelectSchema.safeParse(data);
};
